import React from "react";
import { motion } from "framer-motion";
import Spline from '@splinetool/react-spline';
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { LinkedinIcon, Users, Target, Layers } from "lucide-react";

const AboutSection = () => {
  const teamMembers = [
    {
      id: 1,
      name: "Arun Singh",
      role: "Founder & CEO",
      image: "https://customer-assets.emergentagent.com/job_godquantum-stealth/artifacts/vm5qxvxf_aruns_headshot.png",
      linkedin: "https://linkedin.com/in/arunsingh23"
    },
    {
      id: 2,
      name: "Team Member 2",
      role: "CTO & Co-founder",
      image: "https://ui-avatars.com/api/?name=&background=1a1a1a&color=00FFD1&size=200&format=png",
      linkedin: "#"
    },
    {
      id: 3,
      name: "Team Member 3", 
      role: "Head of Quantum Physics",
      image: "https://ui-avatars.com/api/?name=&background=1a1a1a&color=00FFD1&size=200&format=png",
      linkedin: "#"
    },
    {
      id: 4,
      name: "Team Member 4",
      role: "Lead Software Architect",
      image: "https://ui-avatars.com/api/?name=&background=1a1a1a&color=00FFD1&size=200&format=png", 
      linkedin: "#"
    },
    {
      id: 5,
      name: "Team Member 5",
      role: "VP of Engineering",
      image: "https://ui-avatars.com/api/?name=&background=1a1a1a&color=00FFD1&size=200&format=png",
      linkedin: "#"
    }
  ];

  return (
    <section id="about" className="py-24 border-t border-white/5 bg-white/[0.02] relative overflow-hidden">
      {/* Background 3D Element */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute right-0 top-0 w-1/2 h-full">
          <Spline
            scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="mb-6 bg-emerald-500/10 text-emerald-300 border-emerald-500/20">
            About Godquantum Systems
          </Badge>
          <h2 className="display-medium mb-6">Building the Future of Quantum Computing</h2>
          <div className="max-w-4xl mx-auto">
            <p className="body-large text-gray-300 leading-relaxed">
              We are a team of passionate <span className="text-emerald-300">system architects</span>, 
              <span className="text-emerald-300"> software engineers</span>, 
              <span className="text-emerald-300"> theoretical physicists</span>, 
              <span className="text-emerald-300"> quantum physicists</span>, 
              <span className="text-emerald-300"> power electronics</span> and 
              <span className="text-emerald-300"> chip designers</span> working towards creating 
              advanced deep tech full-stack quantum computing with a unified platform engineering 
              approach to create Global Hyperscaler <span className="text-emerald-400 font-semibold">Quantum OS - DCaaS</span>.
            </p>
          </div>
        </motion.div>

        {/* Mission Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {[
            {
              icon: <Target className="w-8 h-8" />,
              title: "Our Mission",
              description: "To democratize quantum computing through accessible, fault-tolerant systems that solve humanity's most complex challenges."
            },
            {
              icon: <Layers className="w-8 h-8" />,
              title: "Our Approach", 
              description: "Full-stack quantum platform engineering with unified architecture spanning hardware, software, and cloud infrastructure."
            },
            {
              icon: <Users className="w-8 h-8" />,
              title: "Our Impact",
              description: "Accelerating breakthroughs in AI/ML, drug discovery, financial optimization, and scientific research through quantum advantage."
            }
          ].map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 bg-black/40 border-white/10 dark-hover dark-transition h-full">
                <div className="text-emerald-400 mb-4">{item.icon}</div>
                <h3 className="heading-2 mb-4 text-white">{item.title}</h3>
                <p className="body-medium text-gray-300">{item.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Team Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="display-medium mb-6">Our Team</h3>
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Leading experts in quantum computing, system architecture, and deep technology development
          </p>
        </motion.div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <Card className="p-6 bg-black/40 border-white/10 dark-hover dark-transition">
                {/* Profile Image */}
                <div className="relative mb-4 mx-auto w-24 h-24 rounded-2xl overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover filter brightness-110 group-hover:brightness-125 transition-all duration-300"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=00FFD1&color=000&size=200`;
                    }}
                  />
                </div>
                
                {/* Member Info */}
                <h4 className="heading-3 mb-2 text-white">{member.name}</h4>
                <p className="body-small text-emerald-300 mb-4">{member.role}</p>
                
                {/* LinkedIn Link */}
                <a 
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-300 transition-colors"
                >
                  <LinkedinIcon className="w-4 h-4" />
                  <span className="text-xs">Connect</span>
                </a>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Technology Stack */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <h4 className="heading-2 mb-8 text-white">Our Technology Stack</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              "Quantum Control Systems", "Cryogenic Engineering", "Microwave Electronics", 
              "Distributed Computing", "Machine Learning", "Blockchain Security", 
              "Cloud Infrastructure", "API Development", "Real-time Systems", "Hardware Design"
            ].map((tech, index) => (
              <div 
                key={tech}
                className="bg-black/60 border border-white/10 rounded-lg px-4 py-3 text-center text-sm text-gray-300 hover:border-emerald-500/30 hover:text-emerald-300 transition-colors"
              >
                {tech}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;